import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { DEFAULT_WEBSOCKET_CONFIG, WebSocketConfig } from './websocket.config';

type EnvironmentWithWebSocket = typeof environment & {
  websocket?: Partial<WebSocketConfig>;
};

@Injectable({
  providedIn: 'root',
})
export class WebsocketConfigService {
  private readonly config: WebSocketConfig;

  constructor() {
    this.config = this.resolveConfig();
  }

  getConfig(): WebSocketConfig {
    return { ...this.config };
  }

  isEnabled(): boolean {
    return this.config.enabled && !!this.config.url;
  }

  getUrl(): string {
    return this.normalizeWebSocketUrl(this.config.url);
  }

  getReconnectDelayMs(): number {
    return this.config.reconnectDelayMs;
  }

  getMaxReconnectAttempts(): number {
    return this.config.maxReconnectAttempts;
  }

  getHeartbeatIntervalMs(): number {
    return this.config.heartbeatIntervalMs;
  }

  /** JWT / session token for WebSocket auth (same keys as hygiene REST APIs). */
  getAuthToken(): string {
    const keys = ['updated', 'jwtToken', 'token', 'authToken', 'accessToken'] as const;
    for (const key of keys) {
      const raw = sessionStorage.getItem(key) || localStorage.getItem(key);
      if (raw?.trim()) {
        return raw.trim();
      }
    }
    return '';
  }

  /** Raw JWT for query params / STOMP headers (matches HTTP interceptor). */
  getAuthTokenQueryValue(): string {
    return this.getAuthToken().replace(/^Bearer\s+/i, '').trim();
  }

  /** HTTP SockJS base URL (no /websocket suffix). */
  getSockJsHttpBase(): string {
    const env = environment as EnvironmentWithWebSocket;
    const raw = (env.websocket?.url || this.config.url || '').trim();
    if (!raw) {
      return '';
    }

    return raw.replace(/^wss:/i, 'https:').replace(/^ws:/i, 'http:').replace(/\/+$/, '');
  }

  /** Builds Spring SockJS raw WebSocket transport URL after /info handshake. */
  async resolveSockJsWebSocketUrl(): Promise<string> {
    const httpBase = this.getSockJsHttpBase();
    if (!httpBase) {
      return '';
    }

    const token = this.getAuthTokenQueryValue();
    const tokenQuery = token
      ? `?token=${encodeURIComponent(token)}&access_token=${encodeURIComponent(token)}`
      : '';

    try {
      const infoResponse = await fetch(`${httpBase}/info${tokenQuery}`, {
        credentials: 'include',
        headers: token ? { Authorization: token } : undefined,
      });
      if (!infoResponse.ok) {
        console.warn('[WebSocket] SockJS info failed:', infoResponse.status, infoResponse.statusText);
      }
    } catch (err) {
      console.warn('[WebSocket] SockJS info request error', err);
    }

    const server = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
    const session = this.randomSessionId(8);
    const wsBase = httpBase.replace(/^https:/i, 'wss:').replace(/^http:/i, 'ws:');
    return `${wsBase}/${server}/${session}/websocket${tokenQuery}`;
  }

  /** STOMP/WebSocket connection URL with auth query params. */
  getConnectionUrl(): string {
    let baseUrl = this.getUrl();
    if (!baseUrl) {
      return '';
    }

    // Fallback when async SockJS resolution is not used.
    if (/\/ws-sockjs\/?$/i.test(baseUrl)) {
      baseUrl = `${baseUrl.replace(/\/+$/, '')}/websocket`;
    }

    const token = this.getAuthTokenQueryValue();
    if (!token) {
      return baseUrl;
    }

    const separator = baseUrl.includes('?') ? '&' : '?';
    const auth = encodeURIComponent(token);
    return `${baseUrl}${separator}token=${auth}&access_token=${auth}`;
  }

  private randomSessionId(length: number): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /** HTTP origin for dashboard REST APIs (derived from WebSocket URL). */
  getHttpOrigin(): string {
    const wsUrl = this.getUrl().trim();
    if (wsUrl) {
      return wsUrl
        .replace(/^wss:/i, 'https:')
        .replace(/^ws:/i, 'http:')
        .replace(/\/ws-sockjs\/?$/i, '')
        .replace(/\/ws\/?$/i, '');
    }

    const httpBase =
      localStorage.getItem('backendBaseURL') ||
      localStorage.getItem('WebService') ||
      (environment as EnvironmentWithWebSocket).employeeURL ||
      '';

    return (httpBase || '').trim().replace(/\/+$/, '');
  }

  /** Re-resolve URL after login when backendBaseURL is set in localStorage. */
  refresh(): void {
    Object.assign(this.config, this.resolveConfig());
  }

  private resolveConfig(): WebSocketConfig {
    const env = environment as EnvironmentWithWebSocket;
    const envWs = env.websocket ?? {};
    const merged: WebSocketConfig = {
      ...DEFAULT_WEBSOCKET_CONFIG,
      ...envWs,
    };

    const explicitUrl = (merged.url || '').trim();
    if (explicitUrl) {
      return { ...merged, url: this.normalizeWebSocketUrl(explicitUrl) };
    }

    const httpBase =
      localStorage.getItem('backendBaseURL') ||
      localStorage.getItem('WebService') ||
      env.employeeURL ||
      '';

    return {
      ...merged,
      url: this.toWebSocketUrl(httpBase, merged.path),
    };
  }

  private toWebSocketUrl(httpUrl: string, path: string): string {
    const base = (httpUrl || '').trim().replace(/\/+$/, '');
    if (!base) {
      return '';
    }

    const wsBase = base.replace(/^https:/i, 'wss:').replace(/^http:/i, 'ws:');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return this.normalizeWebSocketUrl(`${wsBase}${normalizedPath}`);
  }

  /** Native WebSocket requires ws/wss scheme (env may use https URL for SockJS endpoint). */
  private normalizeWebSocketUrl(url: string): string {
    const trimmed = (url || '').trim();
    if (!trimmed) {
      return '';
    }

    return trimmed.replace(/^https:/i, 'wss:').replace(/^http:/i, 'ws:');
  }
}
